/**
 * Shared live preview + apply state for split-pane editing tabs.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from 'react';
import type { FlowOptimizerConfig, FlowOptimizerPreviewResult } from '@trafficpulse/shared';

import { buildPreviewDraft } from '../utils/build-preview-draft';
import {
  useFlowOptimizerApply,
  useLivePreview,
  useResetApplyOnConfigEdit,
} from '../hooks';
import type { FlowOptimizerProvider } from '../services/flow-optimizer-api';
import type { UseLivePreviewResult } from '../hooks/useLivePreview';
import { useOptionalStandardDraftContext } from '../standard-draft';
import { skippingGroupNamesByBankId } from '../ignore-paths';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  ApplyReportDialog,
  applyHeldReason,
  isApplyPushed,
  useApplyOutcome,
} from '../apply-report';
import { usePreSaveApply } from './use-pre-save-apply';
import { subFormulaMap } from '@/features/flow-optimizer/context/sub-formula-map';

import { useToast } from '@/providers/ToastProvider';

interface LivePreviewCtx {
  live: UseLivePreviewResult;
  result: FlowOptimizerPreviewResult | null;
  applied: boolean;
  applyPending: boolean;
  onApply: () => void;
  bounds: { minWeight: number; maxWeight: number };
  /** Subformula id -> {name, expression}, so the score popup can humanize
   *  `__sub_…` tokens and show each group's underlying formula. */
  subFormulas: ReadonlyMap<string, { name: string; expression: string }>;
  /** Path bank id -> names of the formula groups that skip it, so the score
   *  popup can say which group is missing from a path's breakdown. */
  skippedGroups: ReadonlyMap<string, string[]>;
}

const FlowOptimizerLivePreviewContext = createContext<LivePreviewCtx | null>(
  null,
);

export function useFlowOptimizerLivePreview(): LivePreviewCtx {
  const ctx = useContext(FlowOptimizerLivePreviewContext);
  if (!ctx) {
    throw new Error('FlowOptimizerLivePreviewProvider is required');
  }
  return ctx;
}

/** Optional access for hover cards that also render outside the provider. */
export function useOptionalFlowOptimizerLivePreview(): LivePreviewCtx | null {
  return useContext(FlowOptimizerLivePreviewContext);
}

export interface FlowOptimizerLivePreviewProviderProps {
  campaignId: string;
  provider: FlowOptimizerProvider;
  draft: FlowOptimizerConfig;
  children: ReactNode;
}

export const FlowOptimizerLivePreviewProvider: FC<
  FlowOptimizerLivePreviewProviderProps
> = ({ campaignId, provider, draft, children }) => {
  const standardDraft = useOptionalStandardDraftContext();
  const flowOverride = standardDraft?.draftState.dirty
    ? standardDraft.draftState.draft
    : null;
  const previewDraft = useMemo(
    () => buildPreviewDraft(draft, flowOverride),
    [draft, flowOverride],
  );
  const live = useLivePreview({
    campaignId,
    provider,
    draft: previewDraft,
    debounceMs: 500,
    enabled: draft.mode === 'CUSTOM',
  });
  const applyMutation = useFlowOptimizerApply(campaignId, provider);
  // Drop the applied snapshot once the user edits exclusions / formula / bounds,
  // so the panel returns to the live preview instead of freezing until reopen.
  useResetApplyOnConfigEdit(draft, applyMutation);
  const result = applyMutation.data ?? live.data;
  const toast = useToast();
  const outcome = useApplyOutcome(provider);

  // Apply rewrote the live flow (weights + disabled toggles); re-seed the editor
  // draft from a fresh fetch so it reflects reality without a reopen. (The
  // backend busts the raw-flow cache on apply, so this fetch is fresh.)
  const reseedDraftFromLive = useCallback(async () => {
    if (!standardDraft) return;
    const refetched = await standardDraft.flowQuery.refetch();
    if (refetched.data) standardDraft.draftState.reseed(refetched.data);
  }, [standardDraft]);

  const runOptimizeApply = useCallback(() => {
    applyMutation.mutate(undefined, {
      onSuccess: async (data) => {
        outcome.announce(data);
        // A held apply (Tres Commas) wrote nothing: keep the unsaved draft and
        // drop the held result so the panel shows the draft's live preview.
        if (applyHeldReason(provider, data)) {
          applyMutation.reset();
          return;
        }
        await reseedDraftFromLive();
      },
      // The backend names the exact reason (which path, which lander) — show
      // its envelope message, not axios' "Request failed with status code 400".
      onError: (err) => toast.error('Apply failed', getApiErrorMessage(err)),
    });
  }, [applyMutation, toast, outcome, reseedDraftFromLive, provider]);

  // Saves a dirty Standard draft to the drawer's tracker first (see the hook).
  const { onApply, pending: preSavePending } = usePreSaveApply({
    provider,
    flowId: draft.flowId,
    dirtyDraft: flowOverride,
    runApply: runOptimizeApply,
  });

  // Subformula id -> {name, expression, weight} for the score hover.
  const subFormulas = useMemo(() => subFormulaMap(draft.formula), [draft.formula]);

  const skippedGroups = useMemo(
    () => skippingGroupNamesByBankId(draft.formula),
    [draft.formula],
  );

  const value = useMemo(
    (): LivePreviewCtx => ({
      live,
      result,
      // A held Tres Commas apply (201, applied:false) is not "applied".
      applied: isApplyPushed(provider, applyMutation.data),
      applyPending: applyMutation.isPending || preSavePending,
      onApply,
      bounds: {
        minWeight: draft.minWeight,
        maxWeight: draft.maxWeight,
      },
      subFormulas,
      skippedGroups,
    }),
    [
      provider,
      live,
      result,
      applyMutation.data,
      applyMutation.isPending,
      preSavePending,
      onApply,
      draft.minWeight,
      draft.maxWeight,
      subFormulas,
      skippedGroups,
    ],
  );

  return (
    <FlowOptimizerLivePreviewContext.Provider value={value}>
      {children}
      <ApplyReportDialog report={outcome.partialReport} onClose={outcome.dismiss} />
    </FlowOptimizerLivePreviewContext.Provider>
  );
};
